import React, { useEffect, useRef, useState } from "react";
import { Activity, Check, ChevronDown } from "lucide-react";

/**
 * StreamHealthPill — compact live health indicator + quality selector
 * for the mobile studio stage (iter214).
 *
 * Shows: colored health dot · resolution · fps · outbound bitrate.
 * Tap → quality sheet (Auto 1080p / 1080p·60 / 720p / Battery saver).
 * Stats come from broadcasterProxy.getOutboundStats() (WebRTC
 * outbound-rtp when viewers are connected, local track settings when
 * broadcasting to zero viewers).
 */

export const QUALITY_PRESETS = {
  auto:    { label: "Auto · 1080p",   width: 1080, height: 1920, fps: 30 },
  fps60:   { label: "1080p · 60fps",  width: 1080, height: 1920, fps: 60 },
  p720:    { label: "720p · saver",   width: 720,  height: 1280, fps: 30 },
  battery: { label: "Battery saver",  width: 540,  height: 960,  fps: 24 },
};

export default function StreamHealthPill({ broadcasterProxy, qualityKey = "auto", onSelectQuality }) {
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const lastRef = useRef({ bytes: 0, ts: 0 });

  useEffect(() => {
    if (!broadcasterProxy) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await broadcasterProxy.getOutboundStats?.();
        if (cancelled || !s) return;
        let kbps = 0;
        const last = lastRef.current;
        if (last.ts && s.timestamp > last.ts && s.bytesSent >= last.bytes) {
          kbps = Math.round(((s.bytesSent - last.bytes) * 8) / (s.timestamp - last.ts));
        }
        lastRef.current = { bytes: s.bytesSent, ts: s.timestamp };
        setStats({ ...s, kbps });
      } catch { /* stats are best-effort */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [broadcasterProxy]);

  const dot = !stats
    ? "bg-white/40"
    : stats.viewers === 0
      ? "bg-emerald-400"
      : stats.kbps > 1500 && stats.fps >= 20
        ? "bg-emerald-400"
        : stats.kbps > 500 && stats.fps >= 10
          ? "bg-amber-400"
          : "bg-red-500";

  const res = stats?.height ? `${Math.min(stats.width, stats.height)}p` : "—";
  const fps = stats?.fps ? `${Math.round(stats.fps)}fps` : "";
  const rate = stats?.viewers > 0 && stats?.kbps
    ? stats.kbps >= 1000 ? `${(stats.kbps / 1000).toFixed(1)}Mb` : `${stats.kbps}kb`
    : "";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="stream-health-pill"
        className="bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1.5"
        title="Stream health — tap to change quality"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <Activity className="w-2.5 h-2.5 text-white/60" />
        {res}{fps ? ` · ${fps}` : ""}{rate ? ` · ${rate}` : ""}
        <ChevronDown className={`w-2.5 h-2.5 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="stream-quality-sheet"
          className="absolute top-full left-0 mt-1.5 z-30 bg-black/85 backdrop-blur-md border border-white/15 rounded-xl overflow-hidden min-w-[168px]"
        >
          <div className="px-3 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-white/40">Stream quality</div>
          {Object.entries(QUALITY_PRESETS).map(([key, cfg]) => (
            <button
              key={key}
              type="button"
              onClick={() => { onSelectQuality?.(key, cfg); setOpen(false); }}
              data-testid={`stream-quality-${key}`}
              className={`w-full text-left px-3 py-2 text-[11px] font-bold flex items-center justify-between gap-3 ${
                qualityKey === key ? "text-tv-accent bg-white/5" : "text-white/85 hover:bg-white/10"
              }`}
            >
              {cfg.label}
              {qualityKey === key && <Check className="w-3 h-3" />}
            </button>
          ))}
          <div className="px-3 pb-2 pt-1 text-[9px] text-white/35 leading-snug">
            60fps needs a recent phone. Changes apply live.
          </div>
        </div>
      )}
    </div>
  );
}
